import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Link,
  Grid,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { AppLayout } from './UI';

interface CreditsPageProps {
  onNavigate?: (view: string) => void;
}

export const CreditsPage: React.FC<CreditsPageProps> = ({ onNavigate }) => {
  return (
    <AppLayout
      title="Credits"
      showSyncButton={false}
      currentView="credit"
      onNavigate={onNavigate}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Credits
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Informazioni sullo sviluppo dell'applicazione
          </Typography>
        </Box>

        {/* D-Serviceitalia Card */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <BusinessIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  D-Service Italia
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Sviluppo Software & Consulenza IT
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Informazioni Aziendali */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Informazioni Aziendali
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Partita IVA:</strong> 02520560810
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Capitale Sociale:</strong> 60.000 € i.v.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Contatti
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Telefono:</strong> +39 091 929 5736
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Email:</strong> info@d-serviceitalia.it
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>PEC:</strong> info@pec.d-serviceitalia.it
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Sito Web:</strong>{' '}
                      <Link 
                        href="https://www.d-serviceitalia.it" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                      >
                        www.d-serviceitalia.it
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Fondazione Card */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 56, height: 56 }}>
                <BusinessIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  Fondazione Roberta Mucera ETS
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Committente dell'applicazione
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="body1" paragraph>
              Questa applicazione è stata sviluppata per conto della <strong>Fondazione Roberta Mucera ETS</strong>, 
              un Ente del Terzo Settore impegnato nella raccolta di donazioni per scopi benefici.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Chip label="Ente del Terzo Settore" color="success" size="small" />
              <Chip label="Repertorio n. 144574" color="success" size="small" />
              <Chip label="C.F. e P.IVA: 97384690828" color="success" size="small" />
            </Box>
          </CardContent>
        </Card>


        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            © 2025 D-Service Italia - Tutti i diritti riservati
          </Typography>
        </Box>
      </Box>
    </AppLayout>
  );
};
